<template>
  <div class="extra-images-card">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="5"
            width="18"
            height="14"
            rx="3"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <circle
            cx="9"
            cy="10"
            r="2"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <path
            d="m5.5 17 4.2-4 3.2 2.8 2.5-2.2L19 17"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Галерея компании
        </span>

        <h3 class="step-title">
          Дополнительные фотографии
        </h3>
      </div>

      <span class="optional-badge">
        Необязательно
      </span>
    </div>

    <p class="description">
      Добавьте фотографии офиса, команды или корпоративных мероприятий.
      Они помогут кандидатам почувствовать атмосферу вашей компании.
    </p>

    <!-- Информация -->
    <div class="gallery-heading">
      <div class="gallery-counter">
        <span class="counter-value">
          {{ uploadedCount }}
        </span>

        <span class="counter-divider">/</span>
        <span>3 фото</span>
      </div>

      <span class="formats">
        JPG, PNG или WEBP · до 10 МБ
      </span>
    </div>

    <!-- Галерея -->
    <div class="images-grid">
      <div
        v-for="(_, index) in slots"
        :key="index"
        class="image-slot"
        :class="{
          filled: Boolean(model.extraImages[index]),
          uploading: model.isUploadingExtra[index],
          failed: Boolean(uploadErrors[index])
        }"
        role="button"
        tabindex="0"
        :aria-label="getSlotLabel(index)"
        @click="selectImage(index)"
        @keydown.enter.prevent="selectImage(index)"
        @keydown.space.prevent="selectImage(index)"
      >
        <!-- Изображение -->
        <Transition name="image-preview">
          <img
            v-if="model.extraImages[index]"
            :src="model.extraImages[index]"
            :alt="`Дополнительное фото ${index + 1}`"
            class="slot-image"
          />
        </Transition>

        <!-- Placeholder -->
        <div
          v-if="!model.extraImages[index]"
          class="slot-placeholder"
        >
          <div class="add-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 6v12M6 12h12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <span class="placeholder-title">
            Добавить фото
          </span>

          <span class="placeholder-index">
            {{ index + 1 }}
          </span>
        </div>

        <!-- Hover -->
        <div
          v-if="
            model.extraImages[index] &&
            !model.isUploadingExtra[index]
          "
          class="image-hover"
        >
          <div class="replace-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 16.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />

              <path
                d="m8 10 4-4 4 4M12 6v9"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <span>Заменить</span>
        </div>

        <!-- Удаление -->
        <Transition name="remove-button">
          <button
            v-if="
              model.extraImages[index] &&
              !model.isUploadingExtra[index]
            "
            type="button"
            class="remove-image-button"
            aria-label="Удалить фотографию"
            @click.stop="removeImage(index)"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </Transition>

        <!-- Номер фотографии -->
        <div
          v-if="model.extraImages[index]"
          class="image-number"
        >
          {{ index + 1 }}
        </div>

        <!-- Загрузка -->
        <Transition name="upload-overlay">
          <div
            v-if="model.isUploadingExtra[index]"
            class="upload-overlay"
          >
            <div class="upload-loader">
              <div class="loader-ring"></div>

              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m8 11 4-4 4 4M12 7v10"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <span>Загружаем</span>
          </div>
        </Transition>

        <!-- Ошибка -->
        <Transition name="error-overlay">
          <div
            v-if="
              uploadErrors[index] &&
              !model.isUploadingExtra[index]
            "
            class="error-overlay"
          >
            <div class="error-icon">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <path
                  d="M10 6.2v4.5M10 13.6v.1"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>

            <span>{{ uploadErrors[index] }}</span>

            <button
              type="button"
              class="retry-button"
              @click.stop="selectImage(index)"
            >
              Попробовать снова
            </button>
          </div>
        </Transition>

        <!-- Успешная загрузка -->
        <Transition name="success-badge">
          <div
            v-if="
              model.extraImageURLs[index] &&
              !model.isUploadingExtra[index] &&
              !uploadErrors[index]
            "
            class="success-badge"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Общая ошибка -->
    <Transition name="message">
      <div
        v-if="fileError"
        class="file-error"
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="10"
            cy="10"
            r="8"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <path
            d="M10 6.2v4.5M10 13.6v.1"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>

        {{ fileError }}
      </div>
    </Transition>

    <!-- Подсказка -->
    <div class="gallery-hint">
      <div class="hint-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 18h6M10 21h4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M8.5 15.5c-1.5-1.1-2.5-2.9-2.5-5a6 6 0 1 1 12 0c0 2.1-1 3.9-2.5 5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <p>
        Лучше всего подходят горизонтальные фотографии с хорошим освещением.
        Нажмите на загруженное изображение, чтобы заменить его.
      </p>
    </div>

    <!-- Один скрытый input для всех слотов -->
    <input
      ref="fileInput"
      type="file"
      class="hidden-input"
      accept="image/jpeg,image/png,image/webp"
      @change="onPicked"
    />
  </div>
</template>

<script setup>
import {
  computed,
  reactive,
  ref
} from "vue";

import CompanyService from "../../api/CompanyService";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

const slots = Array.from({ length: 3 });

const fileInput = ref(null);
const fileError = ref("");

const uploadErrors = reactive([
  "",
  "",
  ""
]);

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp"
];

let currentIndex = null;

initializeArrays();

/*
  Шаг необязательный, поэтому он всегда валиден.
*/
emit("valid");

const uploadedCount = computed(() =>
  model.value.extraImages.filter(Boolean).length
);

function initializeArrays() {
  if (!Array.isArray(model.value.extraImages)) {
    model.value.extraImages = [];
  }

  if (!Array.isArray(model.value.extraImageURLs)) {
    model.value.extraImageURLs = [];
  }

  if (!Array.isArray(model.value.isUploadingExtra)) {
    model.value.isUploadingExtra = [];
  }

  while (model.value.extraImages.length < 3) {
    model.value.extraImages.push(null);
  }

  while (model.value.extraImageURLs.length < 3) {
    model.value.extraImageURLs.push(null);
  }

  while (model.value.isUploadingExtra.length < 3) {
    model.value.isUploadingExtra.push(false);
  }
}

function getSlotLabel(index) {
  if (model.value.isUploadingExtra[index]) {
    return `Фотография ${index + 1} загружается`;
  }

  if (model.value.extraImages[index]) {
    return `Заменить фотографию ${index + 1}`;
  }

  return `Добавить фотографию ${index + 1}`;
}

function selectImage(index) {
  if (model.value.isUploadingExtra[index]) {
    return;
  }

  currentIndex = index;
  fileError.value = "";
  uploadErrors[index] = "";

  if (fileInput.value) {
    fileInput.value.value = "";
    fileInput.value.click();
  }
}

async function onPicked(event) {
  const file = event.target.files?.[0];
  const selectedIndex = currentIndex;

  event.target.value = "";

  if (!file || selectedIndex === null) {
    return;
  }

  const validationError = validateFile(file);

  if (validationError) {
    fileError.value = validationError;
    return;
  }

  const previousPreview =
    model.value.extraImages[selectedIndex];

  const previousURL =
    model.value.extraImageURLs[selectedIndex];

  fileError.value = "";
  uploadErrors[selectedIndex] = "";
  model.value.isUploadingExtra[selectedIndex] = true;

  try {
    const preview = await toBase64(file);

    model.value.extraImages[selectedIndex] = preview;

    const uploadedURL =
      await CompanyService.uploadCompanyImage(
        file,
        "background"
      );

    if (!uploadedURL) {
      throw new Error(
        "Сервис не вернул ссылку на изображение"
      );
    }

    model.value.extraImageURLs[selectedIndex] =
      uploadedURL;

    uploadErrors[selectedIndex] = "";

    console.log(
      `✅ Extra image ${selectedIndex + 1} uploaded:`,
      uploadedURL
    );
  } catch (error) {
    console.error(
      `❌ Extra image ${selectedIndex + 1} upload failed:`,
      error
    );

    /*
      Возвращаем предыдущее изображение, если пользователь
      пытался заменить уже загруженную фотографию.
    */
    model.value.extraImages[selectedIndex] =
      previousPreview;

    model.value.extraImageURLs[selectedIndex] =
      previousURL;

    uploadErrors[selectedIndex] =
      "Не удалось загрузить фото";
  } finally {
    model.value.isUploadingExtra[selectedIndex] =
      false;

    emit("valid");
  }
}

function removeImage(index) {
  if (model.value.isUploadingExtra[index]) {
    return;
  }

  model.value.extraImages[index] = null;
  model.value.extraImageURLs[index] = null;
  model.value.isUploadingExtra[index] = false;

  uploadErrors[index] = "";
  fileError.value = "";

  emit("valid");
}

function validateFile(file) {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Поддерживаются только JPG, PNG и WEBP";
  }

  if (file.size > MAX_FILE_SIZE) {
    return "Размер изображения не должен превышать 10 МБ";
  }

  return "";
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(
        new Error("Не удалось прочитать изображение")
      );
    };

    reader.readAsDataURL(file);
  });
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.extra-images-card {
  width: 100%;
  padding: 28px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  animation: card-appearance 0.45s ease both;
}

/* Header */

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 18px;
}

.step-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.25;
}

.optional-badge {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #687385;
  background: rgba(113, 125, 145, 0.08);

  border: 1px solid rgba(113, 125, 145, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.description {
  max-width: 680px;

  margin: 0 0 22px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.65;
}

/* Gallery heading */

.gallery-heading {
  width: 100%;
  max-width: 680px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  margin-bottom: 11px;
}

.gallery-counter {
  display: flex;
  align-items: baseline;
  gap: 3px;

  color: #8b93a1;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.counter-value {
  color: #3488f6;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
}

.counter-divider {
  color: #bec3cb;
}

.formats {
  color: #9aa1ad;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

/* Gallery */

.images-grid {
  width: 100%;
  max-width: 680px;

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.image-slot {
  position: relative;

  width: 100%;
  aspect-ratio: 1 / 1;

  overflow: hidden;
  cursor: pointer;

  background: #f7f9fc;

  border: 1.5px dashed rgba(51, 132, 245, 0.45);
  border-radius: 19px;

  outline: none;

  transition:
    border-color 0.24s ease,
    background-color 0.24s ease,
    box-shadow 0.24s ease,
    transform 0.24s ease;
}

.image-slot:hover {
  background: rgba(49, 137, 255, 0.045);
  border-color: #388bf6;

  box-shadow:
    0 10px 25px rgba(45, 119, 225, 0.1);

  transform: translateY(-3px);
}

.image-slot:focus-visible {
  border-color: #388bf6;

  box-shadow:
    0 0 0 4px rgba(56, 139, 246, 0.14);
}

.image-slot.filled {
  border-style: solid;
  border-color: rgba(30, 50, 80, 0.08);
}

.image-slot.failed {
  border-color: rgba(220, 76, 76, 0.55);
}

.slot-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition:
    transform 0.45s ease,
    filter 0.3s ease;
}

.image-slot:hover .slot-image {
  transform: scale(1.055);
}

/* Placeholder */

.slot-placeholder {
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 9px;
}

.add-icon {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #398bf6;
  background: rgba(57, 139, 246, 0.09);

  border: 1px solid rgba(57, 139, 246, 0.12);
  border-radius: 14px;

  transition:
    color 0.22s ease,
    background-color 0.22s ease,
    transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.add-icon svg {
  width: 21px;
  height: 21px;
}

.image-slot:hover .add-icon {
  color: #ffffff;
  background: #398bf6;

  box-shadow:
    0 7px 16px rgba(50, 130, 239, 0.22);

  transform: rotate(90deg) scale(1.05);
}

.placeholder-title {
  color: #637087;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.placeholder-index {
  position: absolute;
  top: 10px;
  right: 11px;

  width: 22px;
  height: 22px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8290a5;
  background: rgba(255, 255, 255, 0.8);

  border-radius: 7px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 9px;
}

/* Hover */

.image-hover {
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 7px;

  color: #ffffff;

  background:
    linear-gradient(
      rgba(17, 27, 44, 0.1),
      rgba(17, 27, 44, 0.62)
    );

  opacity: 0;

  transition:
    opacity 0.25s ease;
}

.image-slot:hover .image-hover {
  opacity: 1;
}

.replace-icon {
  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(9px);

  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 12px;

  transform: translateY(6px);

  transition:
    transform 0.25s ease;
}

.image-slot:hover .replace-icon {
  transform: translateY(0);
}

.replace-icon svg {
  width: 20px;
  height: 20px;
}

.image-hover span {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

/* Remove */

.remove-image-button {
  position: absolute;
  top: 9px;
  right: 9px;
  z-index: 8;

  width: 30px;
  height: 30px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  cursor: pointer;

  color: #ffffff;
  background: rgba(24, 29, 39, 0.55);
  backdrop-filter: blur(10px);

  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;

  opacity: 0;

  transition:
    opacity 0.22s ease,
    color 0.22s ease,
    background-color 0.22s ease,
    transform 0.22s ease;
}

.image-slot:hover .remove-image-button,
.remove-image-button:focus-visible {
  opacity: 1;
}

.remove-image-button:hover {
  color: #ffffff;
  background: #df5358;

  transform: scale(1.07);
}

.remove-image-button svg {
  width: 18px;
  height: 18px;
}

.image-number {
  position: absolute;
  bottom: 9px;
  left: 9px;
  z-index: 4;

  width: 25px;
  height: 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: rgba(24, 29, 39, 0.5);
  backdrop-filter: blur(9px);

  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 9px;
}

/* Upload */

.upload-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 10px;

  color: #ffffff;
  background: rgba(22, 32, 49, 0.6);
  backdrop-filter: blur(7px);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.upload-loader {
  position: relative;

  width: 45px;
  height: 45px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-loader svg {
  position: absolute;

  width: 19px;
  height: 19px;
}

.loader-ring {
  position: absolute;
  inset: 0;

  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #ffffff;
  border-radius: 50%;

  animation: spin 0.75s linear infinite;
}

/* Upload error */

.error-overlay {
  position: absolute;
  inset: 0;
  z-index: 9;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 7px;

  padding: 14px;

  color: #ffffff;
  text-align: center;

  background: rgba(93, 30, 34, 0.79);
  backdrop-filter: blur(7px);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.error-icon svg {
  width: 25px;
  height: 25px;
}

.retry-button {
  padding: 6px 9px;

  cursor: pointer;

  color: #ffffff;
  background: rgba(255, 255, 255, 0.14);

  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 8px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 8px;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.22);

  transform: translateY(-1px);
}

.success-badge {
  position: absolute;
  right: 9px;
  bottom: 9px;
  z-index: 5;

  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #35bd77,
      #279a61
    );

  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;

  box-shadow:
    0 5px 12px rgba(30, 141, 83, 0.25);
}

.success-badge svg {
  width: 16px;
  height: 16px;
}

/* Messages */

.file-error {
  width: 100%;
  max-width: 680px;

  display: flex;
  align-items: center;
  gap: 7px;

  margin-top: 11px;
  padding: 9px 11px;

  color: #d54c51;
  background: rgba(222, 75, 81, 0.07);

  border: 1px solid rgba(222, 75, 81, 0.12);
  border-radius: 10px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.file-error svg {
  width: 16px;
  height: 16px;

  flex-shrink: 0;
}

.gallery-hint {
  width: 100%;
  max-width: 680px;

  display: flex;
  align-items: flex-start;
  gap: 10px;

  margin-top: 20px;
  padding: 13px 14px;

  color: #667084;
  background: rgba(53, 132, 247, 0.055);

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 13px;
}

.hint-icon {
  width: 19px;
  height: 19px;

  flex-shrink: 0;

  color: #4388f5;
}

.hint-icon svg {
  width: 19px;
  height: 19px;
}

.gallery-hint p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.55;
}

.hidden-input {
  display: none;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.image-preview-enter-active,
.image-preview-leave-active,
.upload-overlay-enter-active,
.upload-overlay-leave-active,
.error-overlay-enter-active,
.error-overlay-leave-active,
.success-badge-enter-active,
.success-badge-leave-active,
.remove-button-enter-active,
.remove-button-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.image-preview-enter-from {
  opacity: 0;
  transform: scale(1.08);
}

.image-preview-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

.upload-overlay-enter-from,
.upload-overlay-leave-to,
.error-overlay-enter-from,
.error-overlay-leave-to {
  opacity: 0;
}

.success-badge-enter-from,
.success-badge-leave-to {
  opacity: 0;

  transform:
    scale(0.5)
    rotate(-20deg);
}

.remove-button-enter-from,
.remove-button-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Dark mode */

html[data-theme="dark"] .extra-images-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1e;
}

html[data-theme="dark"] .step-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .optional-badge {
  color: #a3aab6;

  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.07);
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .formats,
html[data-theme="dark"] .gallery-counter {
  color: #7f838b;
}

html[data-theme="dark"] .counter-value {
  color: #67a2ff;
}

html[data-theme="dark"] .counter-divider {
  color: #555a63;
}

html[data-theme="dark"] .image-slot {
  background: #25262a;
  border-color: rgba(75, 144, 246, 0.36);
}

html[data-theme="dark"] .image-slot:hover {
  background: #292a2f;
  border-color: #548ff1;

  box-shadow:
    0 11px 27px rgba(0, 0, 0, 0.24);
}

html[data-theme="dark"] .image-slot:focus-visible {
  border-color: #67a2ff;

  box-shadow:
    0 0 0 4px rgba(70, 126, 229, 0.16);
}

html[data-theme="dark"] .image-slot.filled {
  border-color: rgba(255, 255, 255, 0.08);
}

html[data-theme="dark"] .image-slot.failed {
  border-color: rgba(255, 104, 112, 0.6);
}

html[data-theme="dark"] .add-icon {
  color: #67a2ff;

  background: rgba(76, 143, 244, 0.12);
  border-color: rgba(76, 143, 244, 0.16);
}

html[data-theme="dark"] .image-slot:hover .add-icon {
  color: #ffffff;
  background: #398bf6;

  box-shadow:
    0 7px 16px rgba(50, 130, 239, 0.28);
}

html[data-theme="dark"] .placeholder-title {
  color: #aeb5c0;
}

html[data-theme="dark"] .placeholder-index {
  color: #a1a8b3;
  background: rgba(255, 255, 255, 0.065);
}

html[data-theme="dark"] .image-hover {
  background:
    linear-gradient(
      rgba(10, 14, 22, 0.12),
      rgba(10, 14, 22, 0.72)
    );
}

html[data-theme="dark"] .replace-icon {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.22);
}

html[data-theme="dark"] .remove-image-button {
  background: rgba(12, 15, 22, 0.72);
  border-color: rgba(255, 255, 255, 0.14);
}

html[data-theme="dark"] .remove-image-button:hover {
  background: #df5358;
}

html[data-theme="dark"] .image-number {
  background: rgba(12, 15, 22, 0.66);
  border-color: rgba(255, 255, 255, 0.12);
}

html[data-theme="dark"] .upload-overlay {
  background: rgba(8, 11, 18, 0.74);
}

html[data-theme="dark"] .loader-ring {
  border-color: rgba(255, 255, 255, 0.18);
  border-top-color: #ffffff;
}

html[data-theme="dark"] .error-overlay {
  background: rgba(91, 30, 34, 0.84);
}

html[data-theme="dark"] .retry-button {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

html[data-theme="dark"] .retry-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

html[data-theme="dark"] .success-badge {
  border-color: #1c1c1e;

  box-shadow:
    0 5px 14px rgba(30, 141, 83, 0.32);
}

html[data-theme="dark"] .file-error {
  color: #ff8b94;
  background: rgba(222, 75, 81, 0.12);
  border-color: rgba(222, 75, 81, 0.2);
}

html[data-theme="dark"] .gallery-hint {
  color: #9ba2ae;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(83, 104, 232, 0.06)
    );

  border-color: rgba(74, 145, 246, 0.16);
}

html[data-theme="dark"] .hint-icon {
  color: #67a2ff;
}

/* Compact laptop mode
   1366x768 / 1366x788
*/

@media (min-width: 721px) and (max-height: 820px) {
  .extra-images-card {
    padding: 16px 22px;
  }

  .step-header {
    gap: 10px;
    margin-bottom: 9px;
  }

  .step-icon {
    width: 38px;
    height: 38px;

    border-radius: 11px;
  }

  .step-icon svg {
    width: 20px;
    height: 20px;
  }

  .step-heading {
    gap: 1px;
  }

  .step-label {
    font-size: 8px;
  }

  .step-title {
    font-size: 16px;
    line-height: 1.15;
  }

  .optional-badge {
    padding: 5px 8px;

    font-size: 8px;
  }

  .description {
    margin-bottom: 10px;

    font-size: 11px;
    line-height: 1.35;
  }

  .gallery-heading {
    margin-bottom: 7px;
  }

  .gallery-counter {
    font-size: 9px;
  }

  .counter-value {
    font-size: 11px;
  }

  .formats {
    font-size: 8px;
  }

  .images-grid {
    gap: 10px;
  }

  .image-slot {
    aspect-ratio: auto;
    height: 175px;

    border-radius: 14px;
  }

  .slot-placeholder {
    gap: 6px;
  }

  .add-icon {
    width: 34px;
    height: 34px;

    border-radius: 10px;
  }

  .add-icon svg {
    width: 18px;
    height: 18px;
  }

  .placeholder-title {
    font-size: 9px;
  }

  .placeholder-index {
    top: 7px;
    right: 7px;

    width: 19px;
    height: 19px;

    border-radius: 6px;

    font-size: 8px;
  }

  .replace-icon {
    width: 32px;
    height: 32px;

    border-radius: 9px;
  }

  .replace-icon svg {
    width: 17px;
    height: 17px;
  }

  .image-hover {
    gap: 5px;
  }

  .image-hover span {
    font-size: 9px;
  }

  .remove-image-button {
    top: 7px;
    right: 7px;

    width: 26px;
    height: 26px;

    border-radius: 8px;
  }

  .remove-image-button svg {
    width: 15px;
    height: 15px;
  }

  .image-number {
    bottom: 7px;
    left: 7px;

    width: 21px;
    height: 21px;

    border-radius: 6px;

    font-size: 8px;
  }

  .success-badge {
    right: 7px;
    bottom: 7px;

    width: 24px;
    height: 24px;
  }

  .success-badge svg {
    width: 14px;
    height: 14px;
  }

  .upload-overlay {
    gap: 7px;

    font-size: 9px;
  }

  .upload-loader {
    width: 36px;
    height: 36px;
  }

  .upload-loader svg {
    width: 16px;
    height: 16px;
  }

  .loader-ring {
    border-width: 2px;
  }

  .error-overlay {
    gap: 5px;

    padding: 10px;

    font-size: 8px;
  }

  .error-icon svg {
    width: 21px;
    height: 21px;
  }

  .retry-button {
    padding: 5px 7px;

    font-size: 7px;
  }

  .file-error {
    margin-top: 7px;
    padding: 7px 9px;

    font-size: 9px;
  }

  .file-error svg {
    width: 14px;
    height: 14px;
  }

  .gallery-hint {
    gap: 8px;

    margin-top: 10px;
    padding: 9px 11px;

    border-radius: 10px;
  }

  .hint-icon,
  .hint-icon svg {
    width: 16px;
    height: 16px;
  }

  .gallery-hint p {
    font-size: 9px;
    line-height: 1.35;
  }
}

/* Extra compact 1366x768 */

@media (min-width: 721px) and (max-height: 770px) {
  .extra-images-card {
    padding-top: 13px;
    padding-bottom: 13px;
  }

  .step-header {
    margin-bottom: 7px;
  }

  .description {
    margin-bottom: 7px;
  }

  .gallery-heading {
    margin-bottom: 5px;
  }

  .images-grid {
    gap: 8px;
  }

  .image-slot {
    height: 155px;
  }

  .gallery-hint {
    margin-top: 7px;
    padding-top: 7px;
    padding-bottom: 7px;
  }

  .file-error {
    margin-top: 5px;
  }
}

/* Mobile */

@media (max-width: 720px) {
  .extra-images-card {
    padding: 21px 18px;

    border-radius: 20px;
  }

  .step-icon {
    width: 40px;
    height: 40px;

    border-radius: 13px;
  }

  .step-title {
    font-size: 17px;
  }

  .description {
    font-size: 13px;
  }

  .images-grid {
    gap: 10px;
  }

  .image-slot {
    border-radius: 16px;
  }

  .placeholder-title {
    font-size: 9px;
  }

  .add-icon {
    width: 38px;
    height: 38px;

    border-radius: 12px;
  }
}

@media (max-width: 520px) {
  .optional-badge,
  .formats {
    display: none;
  }

  .images-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }

  .image-slot:last-child {
    grid-column: 1 / -1;

    width: calc(50% - 5px);
  }

  .remove-image-button {
    opacity: 1;
  }
}

@media (max-width: 360px) {
  .images-grid {
    grid-template-columns: 1fr;
  }

  .image-slot:last-child {
    grid-column: auto;
    width: 100%;
  }
}

@media (hover: none) {
  .remove-image-button {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .extra-images-card,
  .loader-ring {
    animation: none;
  }

  .image-slot,
  .slot-image,
  .add-icon,
  .replace-icon,
  .remove-image-button,
  .image-preview-enter-active,
  .image-preview-leave-active,
  .upload-overlay-enter-active,
  .upload-overlay-leave-active,
  .error-overlay-enter-active,
  .error-overlay-leave-active,
  .success-badge-enter-active,
  .success-badge-leave-active,
  .remove-button-enter-active,
  .remove-button-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}
</style>